import { z } from "zod";

import { isValidParentPinHash } from "./pinHashFormat";
import type {
  ParentAccessChangeListener,
  ParentAccessStore
} from "./types";

export const PARENT_ACCESS_STORAGE_KEY =
  "little-launch-island.parent-access";
export const PARENT_ACCESS_RECORD_VERSION = 1 as const;

type PersistenceResult<T> = T | Promise<T>;

export interface ParentAccessPersistence {
  getItem(key: string): PersistenceResult<string | null>;
  setItem(key: string, value: string): PersistenceResult<void>;
  removeItem(key: string): PersistenceResult<void>;
  subscribe(
    key: string,
    listener: ParentAccessChangeListener
  ): () => void;
}

export type ParentAccessStorageOperation =
  | "load"
  | "save"
  | "clear"
  | "subscribe";

export class InvalidParentAccessRecordError extends Error {
  override readonly name = "InvalidParentAccessRecordError";

  constructor() {
    super("The stored parent access record is invalid.");
  }
}

export class ParentAccessStorageError extends Error {
  override readonly name = "ParentAccessStorageError";

  constructor(
    readonly operation: ParentAccessStorageOperation,
    options?: ErrorOptions
  ) {
    super("The parent access record could not be accessed.", options);
  }
}

const parentAccessRecordSchema = z.strictObject({
  version: z.literal(PARENT_ACCESS_RECORD_VERSION),
  parentPinHash: z.string().refine(isValidParentPinHash)
});

export class LocalParentAccessStore implements ParentAccessStore {
  private writeQueue: Promise<void> = Promise.resolve();

  constructor(
    private readonly persistence: ParentAccessPersistence,
    private readonly storageKey = PARENT_ACCESS_STORAGE_KEY
  ) {}

  async load(): Promise<string | undefined> {
    await this.writeQueue;

    let serializedRecord: string | null;
    try {
      serializedRecord = await this.persistence.getItem(this.storageKey);
    } catch (error) {
      throw new ParentAccessStorageError("load", { cause: error });
    }

    if (serializedRecord === null) {
      return undefined;
    }

    let rawRecord: unknown;
    try {
      rawRecord = JSON.parse(serializedRecord) as unknown;
    } catch {
      throw new InvalidParentAccessRecordError();
    }

    const record = parentAccessRecordSchema.safeParse(rawRecord);
    if (!record.success) {
      throw new InvalidParentAccessRecordError();
    }

    return record.data.parentPinHash;
  }

  async save(parentPinHash: string): Promise<void> {
    const record = parentAccessRecordSchema.safeParse({
      version: PARENT_ACCESS_RECORD_VERSION,
      parentPinHash
    });
    if (!record.success) {
      throw new InvalidParentAccessRecordError();
    }

    const serializedRecord = JSON.stringify(record.data);
    await this.enqueueWrite("save", () =>
      this.persistence.setItem(this.storageKey, serializedRecord)
    );
  }

  async clear(): Promise<void> {
    await this.enqueueWrite("clear", () =>
      this.persistence.removeItem(this.storageKey)
    );
  }

  subscribe(listener: ParentAccessChangeListener): () => void {
    try {
      return this.persistence.subscribe(this.storageKey, listener);
    } catch (error) {
      throw new ParentAccessStorageError("subscribe", { cause: error });
    }
  }

  private enqueueWrite(
    operation: "save" | "clear",
    write: () => PersistenceResult<void>
  ): Promise<void> {
    const queuedWrite = this.writeQueue.then(async () => {
      try {
        await write();
      } catch (error) {
        throw new ParentAccessStorageError(operation, { cause: error });
      }
    });

    this.writeQueue = queuedWrite.catch(() => undefined);
    return queuedWrite;
  }
}
